import React, { useState, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { authAPI } from './services/api';

// Auth pages
import LoginPage from './pages/LoginPage';

// Teacher pages
import TeacherDashboard from './pages/teacher/TeacherDashboard';
import ManageStudents from './pages/teacher/ManageStudents';
import ExerciseAccess from './pages/teacher/ExerciseAccess';
import StudentDetail from './pages/teacher/StudentDetail';

// Student pages
import StudentDashboard from './pages/student/StudentDashboard';
import ExerciseWorkspace from './pages/student/ExerciseWorkspace';
import QuizPage from './pages/student/QuizPage';
import QuizResults from './pages/student/QuizResults';

import './App.css';

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const handleUnauthorized = () => setUser(null);
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    checkAuth();

    return () => {
      window.removeEventListener('auth:unauthorized', handleUnauthorized);
    };
  }, []);

  const checkAuth = async () => {
    try {
      const response = await authAPI.getCurrentUser();
      setUser(response.data.user);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = (userData) => {
    setUser(userData);
  };

  const handleLogout = async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setUser(null);
    }
  };

  if (loading) {
    return (
      <div className="loading-screen">
        <div className="spinner"></div>
        <p>Loading...</p>
      </div>
    );
  }

  return (
    <Router>
      <Routes>
        {/* Login */}
        <Route 
          path="/login" 
          element={
            user ? 
              <Navigate to={user.role === 'teacher' ? '/teacher/dashboard' : '/student/dashboard'} /> :
              <LoginPage onLogin={handleLogin} />
          } 
        />

        {/* Teacher Routes */}
        <Route 
          path="/teacher/dashboard" 
          element={
            user?.role === 'teacher' ? 
              <TeacherDashboard user={user} onLogout={handleLogout} /> : 
              <Navigate to="/login" />
          } 
        />
        <Route 
          path="/teacher/students" 
          element={
            user?.role === 'teacher' ? 
              <ManageStudents user={user} onLogout={handleLogout} /> : 
              <Navigate to="/login" />
          } 
        />
        <Route 
          path="/teacher/exercise-access" 
          element={
            user?.role === 'teacher' ? 
              <ExerciseAccess user={user} onLogout={handleLogout} /> : 
              <Navigate to="/login" />
          } 
        />
        <Route 
          path="/teacher/students/:studentId" 
          element={
            user?.role === 'teacher' ? 
              <StudentDetail user={user} onLogout={handleLogout} /> : 
              <Navigate to="/login" />
          } 
        />

        {/* Student Routes */}
        <Route 
          path="/student/dashboard" 
          element={
            user?.role === 'student' ? 
              <StudentDashboard user={user} onLogout={handleLogout} /> : 
              <Navigate to="/login" />
          } 
        />
        <Route 
          path="/student/exercises/:exerciseId" 
          element={
            user?.role === 'student' ? 
              <ExerciseWorkspace user={user} onLogout={handleLogout} /> : 
              <Navigate to="/login" />
          } 
        />
        <Route 
          path="/student/exercises/:exerciseId/quiz" 
          element={
            user?.role === 'student' ? 
              <QuizPage user={user} onLogout={handleLogout} /> : 
              <Navigate to="/login" />
          } 
        />
        <Route 
          path="/student/exercises/:exerciseId/results" 
          element={
            user?.role === 'student' ? 
              <QuizResults user={user} onLogout={handleLogout} /> : 
              <Navigate to="/login" />
          } 
        />

        {/* Default redirects */}
        <Route 
          path="/" 
          element={
            <Navigate to={
              user ? 
                (user.role === 'teacher' ? '/teacher/dashboard' : '/student/dashboard') : 
                '/login'
            } />
          } 
        />
        
        <Route path="*" element={<Navigate to="/" />} />
      </Routes>
    </Router>
  );
}

export default App;
